// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the colour picker
//  ---------------------------------------------------------------------------
//  Six fixed swatches in a menu is a palette, not a picker. It offered one
//  version of each hue, so "a slightly deeper green" was a thing a person could
//  want and the app could not hear — and with a dozen statuses in one workflow,
//  telling two greens apart is exactly the job the hue is doing.
//
//  Hues across, shades down, and No colour. Seven families of four, which is
//  small enough to read in one glance and wide enough that two statuses in the
//  same category never have to share.
//
//  ── EVERY SWATCH HAS A NAME ───────────────────────────────────────────────
//  "Violet, deep" — never "#5B49B8". A grid of coloured squares is the one
//  control in this app that is literally invisible to somebody who cannot see
//  the colours, so the name is not a courtesy label: it is the control. The
//  same reason StatusMark draws six SHAPES rather than six hues.
//
//  ── AND IT IS OPERABLE WITHOUT A POINTER ──────────────────────────────────
//  A radiogroup with a roving tabindex: one tab stop for the whole grid, arrows
//  to move within it, and the current colour is where the tab lands. Twenty-nine
//  tab stops for one decision is a control that technically qualifies and
//  nobody could use.
//
//  ── ITS OWN FILE, ON PURPOSE ──────────────────────────────────────────────
//  The Statuses page is the first surface to need this and deliberately not the
//  last: a domain's colour is the same question asked about a different thing.
//  A second dialect for choosing a colour is how two screens in one app end up
//  disagreeing about what "no colour" means.
// ════════════════════════════════════════════════════════════════════════════

(function () {
  'use strict';

  const { useState: useStC, useRef: useRfC, useEffect: useEfC } = React;
  const inkC = (a) => (window.ink ? window.ink(a) : `rgba(127,127,127,${a})`);

  /* Seven families. The four hues the shipped menu offered are all still in
     here, at the shade they were, so a status coloured before this build is
     wearing a colour the picker can still show as chosen. */
  const MD_COLOR_HUES = [
    { id: 'violet', name: 'Violet', shades: ['#C4BBF0', '#A08FEA', '#7C68E6', '#5B49B8'] },
    { id: 'blue', name: 'Blue', shades: ['#9EBEE4', '#7099D2', '#4A7FC1', '#35608F'] },
    { id: 'teal', name: 'Teal', shades: ['#9CD2CE', '#6BBBB4', '#3E9E96', '#2C7570'] },
    { id: 'green', name: 'Green', shades: ['#A2D6B8', '#6FBF92', '#3B9E6E', '#2A7452'] },
    { id: 'amber', name: 'Amber', shades: ['#E4C08C', '#D69F5F', '#C9803A', '#96602B'] },
    { id: 'rust', name: 'Rust', shades: ['#E0A08F', '#D07A63', '#B85C42', '#8A4331'] },
    { id: 'rose', name: 'Rose', shades: ['#E0A3AC', '#CC6E7D', '#B44B5C', '#863845'] },
  ];

  /* Read down, so the row a colour sits in is what its name says. Not "shade
     3": a person reading these aloud to themselves needs words. */
  const MD_COLOR_SHADES = ['soft', 'clear', 'deep', 'dark'];

  /** What one colour is called. Unknown hex — a colour from another build, or
      one somebody typed into a document by hand — is named as itself rather
      than dropped, because it is still what the status is wearing. */
  function mdColorName(value) {
    if (!value) return 'No colour';
    const want = String(value).toUpperCase();
    for (const hue of MD_COLOR_HUES) {
      const at = hue.shades.findIndex((shade) => shade.toUpperCase() === want);
      if (at !== -1) return `${hue.name}, ${MD_COLOR_SHADES[at]}`;
    }
    return String(value);
  }

  /** The grid, and one press.

      `value` is the colour currently held, or null. `onPick` is given a hex or
      null — the same two answers `mdWorkflowTryStatusSetColor` takes, so the
      caller never has to translate. Nothing is drawn optimistically here: the
      caller re-reads and hands the new value back down, which is what makes a
      refused write leave the previous colour showing rather than the one that
      did not save. */
  function MdColorPicker({ value = null, onPick, label = 'Colour' }) {
    const grid = useRfC(null);
    const held = value ? String(value).toUpperCase() : null;
    /* Where the tab lands: the colour in force, or the first swatch. */
    /* SHADE-MAJOR, the order the grid is actually laid out in — hues across a
       row, shades down the columns. Walking the hues first would put the tab on
       whatever swatch happened to share an index with the chosen one. */
    const flat = [null, ...MD_COLOR_SHADES.flatMap((shade, row) =>
      MD_COLOR_HUES.map((hue) => hue.shades[row]))];
    const at = Math.max(0, flat.findIndex((hex) =>
      (hex ? hex.toUpperCase() : null) === held));
    const [focus, setFocus] = useStC(at);
    useEfC(() => { setFocus(at); }, [at]);

    const move = (event) => {
      const cols = MD_COLOR_HUES.length;
      const key = event.key;
      if (key !== 'ArrowRight' && key !== 'ArrowLeft'
        && key !== 'ArrowDown' && key !== 'ArrowUp') return;
      event.preventDefault();
      /* Index 0 is No colour, which stands outside the grid; from it, any arrow
         steps into the first swatch rather than doing nothing. */
      let next = focus;
      if (focus === 0) next = 1;
      else {
        const cell = focus - 1;
        const row = Math.floor(cell / cols), col = cell % cols;
        const step = key === 'ArrowRight' ? [row, col + 1]
          : key === 'ArrowLeft' ? [row, col - 1]
          : key === 'ArrowDown' ? [row + 1, col] : [row - 1, col];
        if (step[0] < 0) next = 0;
        else if (step[0] >= MD_COLOR_SHADES.length || step[1] < 0 || step[1] >= cols) next = focus;
        else next = 1 + step[0] * cols + step[1];
      }
      setFocus(next);
      const node = grid.current && grid.current.querySelector(`[data-md-swatch="${next}"]`);
      if (node) node.focus();
    };

    const swatch = (hex, index) => {
      const chosen = (hex ? hex.toUpperCase() : null) === held;
      return <button key={index} type="button" role="radio" data-md-swatch={index}
        aria-checked={chosen ? 'true' : 'false'} aria-label={mdColorName(hex)}
        tabIndex={focus === index ? 0 : -1}
        onFocus={() => setFocus(index)} onClick={() => onPick(hex)}
        style={{ width: 20, height: 20, borderRadius: 10, cursor: 'pointer', padding: 0,
          background: hex || 'transparent',
          border: chosen ? `2px solid ${inkC(0.85)}` : `1px solid ${inkC(0.2)}`,
          /* No colour is drawn as the absence it is: an empty ring with a
             stroke through it, the same grammar the Cancelled mark uses. */
          backgroundImage: hex ? 'none'
            : `linear-gradient(45deg, transparent 45%, ${inkC(0.45)} 45%, ${inkC(0.45)} 55%, transparent 55%)` }} />;
    };

    /* Columns are hues, so a family reads DOWN — which is what "shades down"
       means to the eye. The grid is filled row by row, one shade at a time. */
    return <div ref={grid} role="radiogroup" aria-label={label} onKeyDown={move}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 10px' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {swatch(null, 0)}
        <span style={{ color: inkC(0.45), fontSize: 12 }}>No colour</span>
      </div>
      {MD_COLOR_SHADES.map((shade, row) =>
        <div key={shade} style={{ display: 'flex', gap: 6 }}>
          {MD_COLOR_HUES.map((hue, col) =>
            swatch(hue.shades[row], 1 + row * MD_COLOR_HUES.length + col))}
        </div>)}
    </div>;
  }

  Object.assign(window, { MD_COLOR_HUES, MD_COLOR_SHADES, mdColorName, MdColorPicker });
})();
